import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';
import { pushButtonClickEventToDataLayer } from '../../common/analytics';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { DialogType, MobileM2MAddonCode, SubscriptionCategory } from '../../common/enums';
import { createSupportCaseAuthenticated } from '../../common/fetch';
import { useChangeOffer } from '../../common/hooks/useChangeOffer';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import {
  campaignPriceMsg,
  connectToRingMsg,
  connectToVakioMsg,
  consumerOmaElisaLowercaseMsg,
  contractDetailsMsg,
  customFormErrorFailedMsg,
  durationOfContractMsg,
  idNumberMsg,
  landlineNotificationMsg,
  listPriceMsg,
  pukCodesMsg,
  ringNotConnectedMsg,
  ringSolutionMsg,
  subscriptionStatusMsg,
  subscriptionTypeMsg,
  t,
  vakioNotConnectedMsg,
  vakioSolutionMsg,
  viewChangeRequestDetailsMsg,
  yourMessageWasSentMsg,
} from '../../common/i18n';
import { CATEGORY_URL } from '../../common/utils/categoryUtils';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils';
import { getRecurringPriceAsTextFromDetails } from '../../common/utils/priceUtils';
import {
  CONSUMER_OMAELISA_LOGIN_URL,
  commercialProductIdForHyllyliittyma,
  getAddonAssociationsAndDependencies,
  getSubscriptionStatus,
  getSubscriptionUrlId,
  isCommitmentPeriodActive,
  isMobileIdAddOn,
  isSubscriptionInSaunalahti,
  shouldDisplayAttachToRing,
  shouldDisplayAttachToVakio,
} from '../../common/utils/subscriptionUtils';
import { PbxType } from '../../generated/api/pbxType';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { SubscriptionType } from '../../generated/api/subscriptionType';
import { SupportCase } from '../../generated/api/supportCase';
import { getCategoryBySubscriptionType } from '../../public/common/util/category';
import { SiteContext } from '../../public/site/SiteContext';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import {
  closeSuspendSubscriptionOptions,
  suspendSubscription,
  terminateSubscriptionAction,
} from '../../selfservice/actions';
import { DetailsWithInfoColumn } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { getCommitmentText } from '../EoeSubscriptionDetails/eppSubscriptionUtils';
import { GridNarrow } from '../Grid/Grid';
import { productVariables } from '../OrderSubscriptionSelection/content/variables';
import { getSubscriptionUrl } from '../SubscriptionLayout/MobileSubscriptionLayout.js';
import { SuspendSubscriptionOptions } from '../SuspendSubscriptionOptions/SuspendSubscriptionOptions';
import { TextWithPopover } from '../TextWithPopover/TextWithPopover';
import { CardContent } from './Components/CardContent';
import {
  SubscriptionDetailsLinkBlocks,
  getBroadbandLinkTypes,
  getVoiceLinkTypes,
} from './SubscriptionDetailsLinkBlocks';
import { SubscriptionDetailsMobileTermination } from './SubscriptionDetailsMobileTermination';
import { TerminateNettiLiteDialog } from './TerminateNettiLiteDialog';
import { getSubscriptionStatusField } from './subscriptionDetailsCommon.js';
import { hasRejectedAddOns } from './subscriptionDetailsPbxCommon.js';

import type { SubscriptionDetailsLinkType } from './SubscriptionDetailsLinkBlocks';
import type { TerminateNettiLiteForm } from './TerminateNettiLiteDialog';
import type { DialogParams } from '../../common/types/dialog';
import type { SubscriptionStatus } from '../../common/types/subscription';
import type { AddOn } from '../../generated/api/addOn';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Subscription } from '../../generated/api/subscription';
import type { State } from '../../selfservice/common/store.js';

import './MobileSubscriptionBasicDetails.scss';

interface BasicDetailsProps {
  subscription: Subscription;
  subscriptionStatus: SubscriptionStatus;
  companyInfo?: CompanyInfoResponse;
  linkTypes: SubscriptionDetailsLinkType[];
  setTerminateNettiLiteDialogVisible: (visible: boolean) => void;
  scheduledChangeUrl?: string;
}

interface ContractDetailsProps {
  subscription: Subscription;
}

interface AttachSubscriptionCardContentProps {
  subscription: Subscription;
  subscriptionStatus: SubscriptionStatus;
  heading: string;
  description: string;
  buttonText: string;
  pbxType: SubscriptionPbxDetails.PbxTypeEnum;
}

interface InfoColumnProps {
  subscription: Subscription;
  subscriptionStatus: SubscriptionStatus;
  companyInfo?: CompanyInfoResponse;
}

interface TerminateNettiLiteDialogProps {
  subscription: Subscription;
  isTerminateDialogVisible: boolean;
  setTerminateDialogVisible: (visible: boolean) => void;
}

export interface MobileSubscriptionBasicDetailsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
  companyInfo?: CompanyInfoResponse;
  hasScheduledChange?: boolean;
  addOnRules: AddOnRulesResponse;
  addOnRulesMobilePbx: AddOnRulesResponse;
}

const PukCard = ({ subscription }: { subscription: Subscription }) => (
  <>
    <h4 className={dsClass.MARGIN_BOTTOM_0}>{t.CVJM(pukCodesMsg)}</h4>
    <p
      className={dsClass.MARGIN_TOP_0}
    >{`PUK1: ${subscription.details?.mobile?.puk1 ?? '—'} | PUK2: ${subscription.details?.mobile?.puk2 ?? '—'}`}</p>
    {isSubscriptionInSaunalahti(subscription) && (
      <p className={dsClass.MARGIN_TOP_4}>
        {t.A06G('You can check the PUK code in ')}
        <Link to={CONSUMER_OMAELISA_LOGIN_URL} target="_blank" rel="noopener noreferrer">
          {t.AS63(consumerOmaElisaLowercaseMsg)}
        </Link>
        .
      </p>
    )}
  </>
);

const populateFupDataLimit = (subscription: Subscription, isLaitenetti: boolean) => {
  if (isLaitenetti) {
    const foundM2MAddOn = subscription.details?.selectedAddOns?.find(addOn =>
      Object.values(MobileM2MAddonCode).some(value => value === addOn.addOnCode)
    );
    return foundM2MAddOn ? productVariables[foundM2MAddOn.addOnCode]?.euFupLimit?.() : '';
  }
  return productVariables[subscription.commercialProductCode || '']?.euFupLimit?.();
};

export const SubscriptionTypeWithPopOver = ({ subscription }: { subscription: Subscription }) => {
  const isLaitenetti = subscription.subscriptionType === SubscriptionType.MOBILE_M2M;
  const fupDataLimit = populateFupDataLimit(subscription, isLaitenetti);

  if (fupDataLimit === undefined) {
    return (
      <div className={dsClass.DISPLAY_FLEX}>
        <div className={dsClass.MARGIN_RIGHT_2}>{t.QH8P(subscriptionTypeMsg)}</div>
      </div>
    );
  }

  return (
    <TextWithPopover
      className="of-subscription-details__popover"
      textContent={t.QH8P(subscriptionTypeMsg)}
      placement="top"
    >
      <p>
        {t.FK90(
          'You can use the internet connection in Europe at domestic prices up to the reasonable usage limit of your subscription.'
        )}
      </p>
      {subscription.commercialProductCode ? (
        <ul className="ds-margin--2">
          <li>
            {subscription.subscriptionName}, {fupDataLimit}
          </li>
        </ul>
      ) : (
        <></>
      )}
      <p className={dsClass.FONT_SIZE_SMALL}>
        {t.O854(
          'When you arrive abroad, you will receive a text message about your reasonable use limit. You will also receive a text message when your data usage limit is about to be reached. After this, you pay 1.30 €/Gb (gigabyte) for data transfer.'
        )}
      </p>
    </TextWithPopover>
  );
};

const BasicDetails = ({
  subscription,
  subscriptionStatus,
  companyInfo,
  linkTypes,
  setTerminateNettiLiteDialogVisible,
  scheduledChangeUrl,
}: BasicDetailsProps) => {
  const { isTerminateSubscriptionForm, showSuspendSubscriptionOptions } = useSelector((state: State) => ({
    isTerminateSubscriptionForm:
      state?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated || false,
    showSuspendSubscriptionOptions:
      state?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated || false,
  }));
  const displayedLinkTypes = !isTerminateSubscriptionForm && !showSuspendSubscriptionOptions ? linkTypes : [];

  return (
    <CL.Grid>
      <CL.GridRow>
        <CL.GridCol className={dsClass.PADDING_LEFT_0} colsS={6} colsM={6} colsL={6}>
          <CL.Description
            className={dsClass.MARGIN_0}
            columns={1}
            items={[
              {
                title: t.LROP(subscriptionStatusMsg),
                description: (
                  <>
                    {getSubscriptionStatusField(subscriptionStatus)}
                    {scheduledChangeUrl && (
                      <Link className={dsClass.DISPLAY_BLOCK} to={scheduledChangeUrl}>
                        {t.CGXR(viewChangeRequestDetailsMsg)}
                      </Link>
                    )}
                  </>
                ),
              },
              { title: t.YTH3(idNumberMsg), description: subscription.subscriptionDisplayId ?? '—' },
            ]}
          />
          {subscription.details?.mobile && (
            <CL.Card className="highlight" content={<PukCard subscription={subscription} />} type="dark" />
          )}
        </CL.GridCol>
        <CL.GridCol className={dsClass.PADDING_RIGHT_0} colsS={6} colsM={6} colsL={6}>
          <SubscriptionDetailsLinkBlocks
            linkTypes={displayedLinkTypes}
            companyInfo={companyInfo}
            subscription={subscription}
            hasPendingActions={subscriptionStatus.hasPendingActions}
            category={getCategoryBySubscriptionType(subscription.subscriptionType)}
            setShowTerminateNettiLite={setTerminateNettiLiteDialogVisible}
          />
        </CL.GridCol>
      </CL.GridRow>
    </CL.Grid>
  );
};

const ContractDetails = ({ subscription }: ContractDetailsProps) => {
  return (
    <>
      <h3 className={classNames(dsClass.H3_BOOK, dsClass.MARGIN_BOTTOM_7)}>{t.KJ47(contractDetailsMsg)}</h3>
      <CL.Description
        className={dsClass.MARGIN_0}
        items={[
          {
            title: <SubscriptionTypeWithPopOver subscription={subscription} />,
            description: subscription.subscriptionName ?? '—',
          },
          {
            title:
              subscription.details &&
              subscription.details.monthlyRecurringListCharge &&
              subscription.details.monthlyRecurringCharge &&
              subscription.details.monthlyRecurringListCharge !== subscription.details.monthlyRecurringCharge
                ? t.SYWR(campaignPriceMsg)
                : t.N68W(listPriceMsg),
            description: getRecurringPriceAsTextFromDetails(subscription.details),
          },
          { title: t.CAA9(durationOfContractMsg), description: getCommitmentText(subscription) },
        ]}
      />
    </>
  );
};

const AttachSubscriptionCardContent = ({
  subscription,
  subscriptionStatus,
  heading,
  description,
  buttonText,
  pbxType,
}: AttachSubscriptionCardContentProps) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const subscriptionId = getSubscriptionUrlId(subscription);
  const [isLoading, setIsLoading] = useState(false);

  const onClick = async () => {
    const redirectionPage = pbxType === SubscriptionPbxDetails.PbxTypeEnum.RING ? 'liitä-ring' : 'liitä-vakio';
    const category = getCategoryBySubscriptionType(subscription.subscriptionType);
    const url = `${paths.PS_HOME}/${CATEGORY_URL[category]}/${subscriptionId}/${redirectionPage}${search}`;
    pushButtonClickEventToDataLayer(
      redirectionPage,
      !subscriptionStatus.hasPendingActions && subscription.subscriptionStatus !== SubscriptionStatusType.IN_ACTIVATION
        ? url
        : undefined
    );
    if (subscriptionStatus.hasPendingActions) {
      openDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
    } else if (subscription.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      openDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
    } else {
      navigate(url);
      // might take a few secs, do not allow clicks when loading
      if (subscriptionStatus.changeOfferAllowed) {
        setIsLoading(true);
      }
    }
  };

  const rejectedAddons = hasRejectedAddOns(subscription);

  return (
    <>
      <h3>{heading}</h3>
      <p>{description}</p>
      {rejectedAddons && <p>{t.ZEU1(landlineNotificationMsg)}</p>}
      <CL.Button
        className={dsClass.MARGIN_TOP_4}
        size="l"
        disabled={rejectedAddons}
        onClick={onClick}
        loading={isLoading}
      >
        {buttonText}
      </CL.Button>
    </>
  );
};

const InfoColumn = ({ subscription, subscriptionStatus, companyInfo }: InfoColumnProps) => {
  return (
    <>
      {shouldDisplayAttachToRing(subscription, companyInfo ?? undefined) && (
        <CL.Card
          className={dsClass.MARGIN_BOTTOM_4}
          content={
            <AttachSubscriptionCardContent
              subscription={subscription}
              subscriptionStatus={subscriptionStatus}
              heading={t.ODIH(ringSolutionMsg)}
              description={t.LO5I(ringNotConnectedMsg)}
              buttonText={t.X0LO(connectToRingMsg)}
              pbxType={SubscriptionPbxDetails.PbxTypeEnum.RING}
            />
          }
          type="outlined"
        />
      )}
      {shouldDisplayAttachToVakio(subscription, companyInfo?.pbxSolutions ?? []) && (
        <CL.Card
          className={dsClass.MARGIN_BOTTOM_4}
          content={
            <AttachSubscriptionCardContent
              subscription={subscription}
              subscriptionStatus={subscriptionStatus}
              heading={t.RFGN(vakioSolutionMsg)}
              description={t.VH8G(vakioNotConnectedMsg)}
              buttonText={t.M0TS(connectToVakioMsg)}
              pbxType={SubscriptionPbxDetails.PbxTypeEnum.VAKIO}
            />
          }
          type="outlined"
        />
      )}
      <CL.Card
        content={<CardContent subscription={subscription} />}
        type="dark"
        analytics-parent-element="OmaElisa instruction card"
      />
    </>
  );
};

const NettiLiteTerminationDialog = ({
  subscription,
  isTerminateDialogVisible,
  setTerminateDialogVisible,
}: TerminateNettiLiteDialogProps) => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname, search } = useLocation();
  const [isSubmittingSupportCase, setSubmittingSupportCase] = useState(false);

  const onTerminateSubscription = async (values: TerminateNettiLiteForm) => {
    setSubmittingSupportCase(true);
    const fixedPeriodMessage = isCommitmentPeriodActive(subscription.details?.commitmentEndDate)
      ? `Määräaikaisuus ${formatTimestampToUTCDDMMYYYY(subscription.details?.commitmentEndDate)} asti`
      : '';
    const description = [
      'Yhteydenottopyyntö Netti Lite -liittymän irtisanomiseksi',
      `Tunnistenumero: ${subscription.subscriptionDisplayId}`,
      fixedPeriodMessage,
      `Osoite: ${subscription.details?.broadband?.installationAddress ?? '-'}\n`,
      `Soittopyynnön puhelinnumero: ${values.msisdn}`,
      `Viesti asiakkaalta:`,
      `${values.message}`,
    ].join('\n');

    try {
      await createSupportCaseAuthenticated(
        {
          feature: SupportCase.FeatureEnum.SUBSCRIPTIONS,
          action: 'Netti Lite -liittymän irtisanominen, yhteydenottopyyntö',
          featureItemId: subscription?.subscriptionId,
          url: `${siteBaseUrl}${pathname}${search}`,
          description,
        },
        mdmId ?? ''
      );
      addNotification(t.Q61D(yourMessageWasSentMsg), 'success');
    } catch (err) {
      addNotification(t.DLRT(customFormErrorFailedMsg), 'error');
    } finally {
      setSubmittingSupportCase(false);
      setTerminateDialogVisible(false);
    }
  };

  return isTerminateDialogVisible ? (
    <TerminateNettiLiteDialog
      isSubmitting={isSubmittingSupportCase}
      onCloseDialog={() => setTerminateDialogVisible(false)}
      fixedTermEndDate={subscription.details?.commitmentEndDate}
      onTerminateNettiLiteSubscription={onTerminateSubscription}
    />
  ) : null;
};

export const MobileSubscriptionBasicDetails = ({
  subscription,
  hasPendingActions,
  hasScheduledChange,
  companyInfo,
  addOnRules,
  addOnRulesMobilePbx,
}: MobileSubscriptionBasicDetailsProps) => {
  const postChangeOffer = useChangeOffer();
  const dispatch = useDispatch();
  const [isTerminateDialogVisible, setTerminateDialogVisible] = useState(false);
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { suspendSubscriptionInitiated, terminateSubscriptionInitiated, terminationInProgress } = useSelector(
    (s: State) => ({
      suspendSubscriptionInitiated: s?.selfservice?.pendingSubscriptionActions?.suspendSubscriptionInitiated ?? false,
      terminateSubscriptionInitiated:
        s?.selfservice?.pendingSubscriptionActions?.terminateSubscriptionInitiated ?? false,
      terminationInProgress: s?.selfservice?.pendingSubscriptionActions?.terminationInProgress ?? false,
    })
  );
  const subscriptionStatus = getSubscriptionStatus(subscription, hasPendingActions, hasScheduledChange);
  const subscriptionCategory = getCategoryBySubscriptionType(subscription.subscriptionType);
  const isVoice = subscriptionCategory === SubscriptionCategory.VOICE;
  const baContractPartyPath = isVoice
    ? paths.PS_MOBILE_SUBSCRIPTION_BA_CONTRACT_PARTY
    : paths.PS_BROADBAND_SUBSCRIPTION_BA_CONTRACT_PARTY;
  const scheduledChangeUrl =
    hasScheduledChange && mdmId ? getSubscriptionUrl(baContractPartyPath, subscription, mdmId) : undefined;
  const linkTypes = isVoice
    ? getVoiceLinkTypes(subscription, subscriptionStatus.changeOfferAllowed)
    : getBroadbandLinkTypes(subscription, subscriptionStatus.changeOfferAllowed);

  if (terminateSubscriptionInitiated) {
    return (
      <GridNarrow>
        <SubscriptionDetailsMobileTermination
          onShowDialog={(params: DialogParams) => openDialog(params)}
          subscription={subscription}
          onTerminateSubscription={(subId, effectiveDate, donateNumber, terminateType) =>
            dispatch(terminateSubscriptionAction(subId, effectiveDate, donateNumber, terminateType))
          }
          terminateSubscriptionLoading={terminationInProgress}
        />
      </GridNarrow>
    );
  } else if (suspendSubscriptionInitiated) {
    const subscriptionType = subscription.subscriptionType as 'MOBILE' | 'MOBILE_BROADBAND';
    const { associations } = getAddonAssociationsAndDependencies(subscription, addOnRules, addOnRulesMobilePbx);
    const hyllyliittymaAddonCodes =
      associations
        ?.filter(assoc => assoc.cspmb__price_item__r.guid__c === commercialProductIdForHyllyliittyma[subscriptionType])
        .filter(assoc => assoc.cspmb__default_quantity__c === 1)
        .map(assoc => assoc.cspmb__add_on_price_item__r.guid__c) || [];

    const selectedAddons: AddOn[] = subscription?.details?.selectedAddOns || [];
    const currentAddonCodes: string[] = selectedAddons.map(addon => addon.addOnCode);
    const isVakio =
      (subscription.compatiblePbxTypes &&
        subscription.compatiblePbxTypes.includes(PbxType.MOBILE_PBX_LITE) &&
        subscription.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails.pbxType ===
          SubscriptionPbxDetails.PbxTypeEnum.VAKIO) ||
      false;
    const hasMobiiliVarmenne = selectedAddons.some(({ addOnGroup, display }) => display && isMobileIdAddOn(addOnGroup));

    return (
      <GridNarrow>
        <SuspendSubscriptionOptions
          hasMobiiliVarmenne={hasMobiiliVarmenne}
          onConfirmSuspend={() =>
            dispatch(suspendSubscription(subscription.subscriptionId, subscription.subscriptionDisplayId))
          }
          onConfirmHyllyliittyma={() =>
            postChangeOffer(
              subscription,
              commercialProductIdForHyllyliittyma[subscriptionType],
              hyllyliittymaAddonCodes,
              currentAddonCodes,
              []
            )
          }
          onCloseSuspendSubscriptionOptions={() => dispatch(closeSuspendSubscriptionOptions())}
          isVakio={isVakio}
          isFixedTermSubscription={isCommitmentPeriodActive(subscription.details?.commitmentEndDate)}
        />
      </GridNarrow>
    );
  }

  return (
    <div className="of-subscription-basic-details">
      <NettiLiteTerminationDialog
        subscription={subscription}
        isTerminateDialogVisible={isTerminateDialogVisible}
        setTerminateDialogVisible={setTerminateDialogVisible}
      />
      <DetailsWithInfoColumn
        infoColumnContent={
          <InfoColumn subscription={subscription} subscriptionStatus={subscriptionStatus} companyInfo={companyInfo} />
        }
      >
        <BasicDetails
          subscription={subscription}
          subscriptionStatus={subscriptionStatus}
          companyInfo={companyInfo}
          linkTypes={linkTypes}
          setTerminateNettiLiteDialogVisible={setTerminateDialogVisible}
          scheduledChangeUrl={scheduledChangeUrl}
        />
        <div className="section-separator" />
        <ContractDetails subscription={subscription} />
      </DetailsWithInfoColumn>
    </div>
  );
};
